import { Component, OnInit } from '@angular/core';
import { PriceRule } from '@app/models/price-rule';
import { DialogBase } from '@shared/helper/dialog-base';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { FormGroup } from '@angular/forms';
import {FormlyFieldConfig, FormlyFormOptions} from '@ngx-formly/core';
import { PriceRuleRepository } from '@app/@core/repositories/price-rule.repository';
import { getPriceRuleForm } from '@app/modules/price-rule/price-rule-form';
import { firstValueFrom, lastValueFrom, Observable } from 'rxjs';
import { isEqual } from 'lodash-es';
import { AppService } from '@shared/services/app.service';

// Utility: Fixes time string to HH:mm format
function fixTimeFormat(time: string): string {
  const [hours, minutes] = time.split(':').map(Number);
  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
}

// Utility: Checks if two arrays are equal regardless of order
function isArrayEqualIgnoreOrder(arr1: number[], arr2: number[]): boolean {
  return isEqual([...arr1].sort(), [...arr2].sort());
}

interface InputData {
  id: string | 'new';
  courseId?: string;
  copyFromId?: string;
  showInstruction?: boolean;
}

interface OutputData {
  action: 'save' | 'delete' | 'cancel';
  priceRule?: PriceRule;
}

@Component({
  selector: 'app-price-rule-edit-dialog',
  templateUrl: './price-rule-edit-dialog.component.html',
  styles: []
})
export class PriceRuleEditDialogComponent extends DialogBase<InputData, OutputData> implements OnInit {
  priceRule: PriceRule;
  showInstruction = true;
  formGroup = new FormGroup({});
  fields: FormlyFieldConfig[];
  relateSlot = 0;

  options: FormlyFormOptions = {
    formState: {
      formModel: undefined
    }
  };

  constructor(
    ref: DynamicDialogRef,
    config: DynamicDialogConfig,
    private app: AppService,
    private priceRuleRepository: PriceRuleRepository
  ) {
    super(ref, config);
  }

  async ngOnInit(): Promise<void> {
    this.showInstruction = this.data.showInstruction ?? true;
    const { id, courseId, copyFromId } = this.data;

    if (id === 'new') {
      await this.initNewRule(courseId, copyFromId);
      this.fields = getPriceRuleForm('NEW');
    } else {
      await this.loadExistingRule(id);
      this.fields = getPriceRuleForm('UPDATE');
    }

    this.options.formState. formModel = this.priceRule;
  }

  private async initNewRule(courseId?: string, copyFromId?: string) {
    if (copyFromId) {
      const source = await firstValueFrom(this.priceRuleRepository.get(copyFromId));
      this.priceRule = {
        ...source,
        id: undefined,
        name: `${source.name}-copy`
      };
      this.app.toastSuccess('1 Price rule copied and ready to edit');
    } else {
      this.app.verify(courseId !== undefined, 'Course ID is required to create a new price rule');
      this.priceRule = {
        priceType: 'WEEKDAY',
        courseId,
        tstart: '06:00',
        tend: '23:00',
        isActive: true,
        cartSetting: {}
      } as PriceRule;
    }
  }

  private async loadExistingRule(id: string) {
    this.priceRule = await lastValueFrom(this.priceRuleRepository.get(id));
    this.relateSlot = await lastValueFrom(this.priceRuleRepository.countSlot(id));
    console.log('this.relateSlot', this.relateSlot);
  }

  async confirmSave() {
    if (this.relateSlot) {
      this.app.confirm(
        'Updating this rule will change prices in related time slots. Continue?',
        async () => {
          const priceRule = await firstValueFrom(this.saveEntity());
          this.app.toastSuccess('Save');
          const slotCount = await firstValueFrom(
            this.priceRuleRepository.applyNewPrice({ priceRuleId: this.priceRule.id })
          );
          this.app.toastSuccess(`Updated ${slotCount} slots`);
          this.close({ action: 'save', priceRule });
        },
        true
      );
    } else {
      this.app.confirmSave(async () => {
        const priceRule = await firstValueFrom(this.saveEntity());
        this.app.toastSuccess('Create');
        this.close({ action: 'save', priceRule });
      });
    }
  }

  private saveEntity(): Observable<PriceRule> {
    this.priceRule.tstart = fixTimeFormat(this.priceRule.tstart);
    this.priceRule.tend = fixTimeFormat(this.priceRule.tend);

    this.priceRule.priceType = isArrayEqualIgnoreOrder(this.priceRule.days, [6, 7])
      ? 'WEEKEND'
      : 'WEEKDAY';

    return this.priceRule.id
      ? this.priceRuleRepository.update(this.priceRule)
      : this.priceRuleRepository.create(this.priceRule);
  }

  deleteEntity() {
    this.app.verify(
      this.relateSlot === 0,
      'This rule is in use. Please remove related time slots before deleting.'
    );
    this.priceRuleRepository.delete(this.priceRule.id).subscribe(() => {
      this.app.toastSuccess('Deleted');
      this.close({ action: 'delete', priceRule: this.priceRule });
    });
  }
}
